import type { GroomingBatchStatus } from "@ttc-spectree/toolbox-contract";
import { api } from "./client";

/**
 * Client for the size-free shared agenda (`GET /api/v1/grooming/shared-agendas/:batchId`;
 * PKG-PM-C, epic PI-SPEC-SW-E291, feature PI-SPEC-SW-1491). The read behind
 * the "Share agenda" link: any signed-in SpecTree user may open it, role or
 * not (01-roles.md section 9.6), so it carries only what an attendee sees on
 * the shared screen. There is deliberately no size field anywhere in these
 * types: Claude's suggested points, range and confidence never reach the page.
 *
 * Kept apart from `./grooming.ts` so the shared agenda never depends on the
 * batch read (`GET /grooming/batches/:id`), whose response carries sizes.
 * Titles and reasons came from tracker items: render as text, never as HTML.
 */

interface DataResponse<T> {
  data: T;
}

/** The statuses a shared agenda can have: a draft agenda, or a run of one (never canceled). */
export type SharedAgendaStatus = Exclude<GroomingBatchStatus, "canceled">;

/** One agenda line, in discussion order. Never a size. */
export interface SharedAgendaItem {
  itemId: string;
  title: string;
  /** The item's discussion order (the batch position). */
  position: number;
  /** Null today: SpecTree stores no item type; the page fills it from the viewer's own ADO read. */
  typeName: string | null;
}

/** One item to fix before the meeting, with why in plain words. */
export interface SharedAgendaFix {
  itemId: string;
  title: string;
  why: string;
}

/** The agenda as an attendee sees it: project, date, items in order and the fix list. */
export interface SharedAgendaView {
  batchId: string;
  project: string;
  status: SharedAgendaStatus;
  /** `YYYY-MM-DD`; null when neither the agenda nor the project has a date. */
  sessionDate: string | null;
  items: SharedAgendaItem[];
  fixes: SharedAgendaFix[];
}

/** The path of the size-free agenda read for `batchId`. */
export function sharedAgendaPath(batchId: string): string {
  return `/grooming/shared-agendas/${encodeURIComponent(batchId)}`;
}

export const groomingSharedAgendaApi = {
  /** The size-free agenda of an agenda batch; sign-in only, no Toolbox role, no ADO PAT. */
  get: (batchId: string) => api.get<DataResponse<SharedAgendaView>>(sharedAgendaPath(batchId)),
};
